// Pasos del tour guiado por rol.
//
// Reglas (ver fases/TOUR_GUIADO.md y lib/tourSteps.test.js):
// - `element` acepta un selector, una lista de selectores (se usa el primero
//   visible; el último de la lista debe existir siempre) o null (globo centrado).
// - Cada selector debe existir en el código: id="…", menuId: '…' o anchor: '…'.
// - Descripciones de 320 caracteres o menos; se permite <ul><li> y <b>.
// - Sin emojis en títulos ni descripciones.
// - `navigate` lleva a la página del paso antes de buscar el ancla. El primer paso de cada rol
//   siempre lo trae: el botón "?" permite iniciar desde cualquier página y los pasos
//   siguientes heredan la página del último `navigate`.
//
// #Fase4-H18 — la ayuda refleja el reporte oficial único (con folio), la
// Vista previa — BORRADOR (no oficial) y el Reporte final del evento.

export const TOUR_STEPS = {
  super_admin: [
    {
      id: 'welcome_dashboard',
      element: '#dashboard-global',
      title: 'Tu panel de control',
      description: 'Aquí ves el estado de tus proyectos, las evaluaciones pendientes y las alertas importantes en un solo lugar.',
      side: 'bottom',
      navigate: '/dashboard',
    },
    {
      id: 'sidebar_navigation',
      // En móvil el sidebar de escritorio está oculto y no tiene variante #mobile-: se cae al
      // primer elemento del menú, que sí abre el drawer.
      element: ['#sidebar-menu', '#menu-dashboard'],
      title: 'Menú por secciones',
      description: '<ul>'
        + '<li><b>Operaciones:</b> Clientes, Proyectos, Eventos y Evaluaciones.</li>'
        + '<li><b>Directorio:</b> Personal, Locaciones, Emergencias, Proveedores y Autoridades.</li>'
        + '<li><b>Administración:</b> Usuarios, Constructor, Auditoría, Wiki, FAQ, Archivo, Modo Espejo y Configuración.</li>'
        + '</ul>',
      side: 'right',
    },
    {
      id: 'clients_module',
      element: '#menu-clientes',
      title: 'Clientes',
      description: 'Cada cliente tiene su perfil: logo, razón social, RFC, representante legal, contacto de emergencias y responsable de seguridad. El interruptor junto a cada uno lo activa o lo desactiva.',
      side: 'right',
      navigate: '/dashboard/clientes',
    },
    {
      id: 'projects_module',
      element: '#menu-proyectos',
      title: 'Proyectos',
      description: 'Un proyecto agrupa al cliente, sus eventos y el equipo. Las evaluaciones no se crean aquí: siempre nacen dentro de un evento. El tablero del proyecto resume evaluaciones, eventos y próximas fechas.',
      side: 'right',
      navigate: '/dashboard/proyectos',
    },
    {
      id: 'events_module',
      element: '#menu-eventos',
      title: 'Eventos',
      description: 'Filtra por cliente arriba. Al crear un evento solo capturas el inicio de montaje y las demás fechas se calculan solas. Cada evento tiene cuatro pestañas: Información, Evaluaciones, Personal y Ubicación (mapa, servicios cercanos y emergencias de la locación).',
      side: 'right',
      navigate: '/dashboard/events',
    },
    {
      id: 'evaluations_global',
      element: '#menu-evaluaciones',
      title: 'Evaluaciones',
      description: 'Todas las evaluaciones del sistema con su cliente, proyecto, evento, tipo, estado y fechas. Según el estado puedes Contestar, Revisar o Ver, y descargar el Reporte oficial con folio cuando esté disponible. Las copias archivadas siguen descargándose.',
      side: 'right',
      navigate: '/dashboard/evaluaciones',
    },
    {
      id: 'staff_module',
      element: '#menu-personal',
      title: 'Personal',
      description: 'Consulta el perfil de tu equipo y asígnalo a eventos por zona y turno. Genera un código QR para que se registren desde su celular; en cada evento, la pestaña Personal muestra a los pendientes con su perfil antes de aprobarlos.',
      side: 'right',
      navigate: '/dashboard/staff',
    },
    {
      id: 'locations_module',
      element: '#menu-locaciones',
      title: 'Locaciones',
      description: 'Ubica cada recinto en el mapa, ajusta el marcador y vincula sus números de emergencia y autoridades. La locación se hereda al evento y a la evaluación.',
      side: 'right',
      navigate: '/dashboard/locations',
    },
    {
      id: 'emergency_module',
      element: '#menu-emergencias',
      title: 'Emergencias y autoridades',
      description: 'Registra hospitales, policía y rescate y vincúlalos a la locación, no al evento: todos los eventos de ese recinto heredan la cobertura. En la pestaña Ubicación de cada evento ves el mapa consolidado con los servicios cercanos.',
      side: 'right',
      navigate: '/dashboard/emergency-services',
    },
    {
      id: 'users_module',
      element: '#menu-usuarios',
      title: 'Usuarios',
      description: 'Crea perfiles completos con datos personales, médicos, bancarios y de emergencia. Los super admin y los líderes también pueden fungir como levantadores en cualquier evento.',
      side: 'right',
      navigate: '/dashboard/usuarios',
    },
    {
      id: 'create_user_button',
      element: '#btn-nuevo-usuario',
      title: 'Crea tu primer usuario',
      description: 'Captura sus datos personales, bancarios y médicos. La barra de progreso te dice cuándo el perfil está completo.',
      side: 'bottom',
    },
    {
      id: 'templates_module',
      element: '#menu-constructor',
      title: 'Constructor (1 de 3): cómo se arma',
      description: 'Aquí se diseñan las listas que contestan los levantadores, en tres niveles: la plantilla (el examen completo), las secciones (por tema) y las preguntas (cada criterio). Secciones y preguntas se reordenan arrastrándolas.',
      side: 'right',
      navigate: '/dashboard/templates',
    },
    {
      id: 'templates_questions',
      element: '#menu-constructor',
      title: 'Constructor (2 de 3): preguntas y bloqueantes',
      description: 'Hay 10 tipos de respuesta; solo la escala 0-10 suma al puntaje. En cada pregunta defines si es requerida, si admite notas y si exige evidencia. Marca como <b>bloqueante</b> un criterio crítico: con calificación 0, la evaluación no se puede aprobar hasta resolverlo.',
      side: 'right',
    },
    {
      id: 'templates_lifecycle',
      element: '#menu-constructor',
      title: 'Constructor (3 de 3): borrador y activa',
      description: 'Una plantilla nace en borrador. Al activarla ya se usa en evaluaciones y deja de editarse. Para corregirla, crea una nueva versión y actívala: la anterior queda retirada y las evaluaciones en curso conservan la suya. Importar JSON carga los criterios de golpe.',
      side: 'right',
    },
    {
      id: 'audit_module',
      element: '#menu-auditoria',
      title: 'Auditoría',
      description: 'Registro de cada cambio: quién, qué y cuándo. Es tu evidencia de lo que pasó en el sistema.',
      side: 'right',
      navigate: '/dashboard/auditoria',
    },
    {
      id: 'wiki_module',
      element: '#menu-wiki',
      title: 'Wiki SOEM',
      description: 'La plataforma explicada módulo por módulo, con buscador. Muestra lo que aplica a tu rol; el interruptor de arriba enseña todo.',
      side: 'right',
      navigate: '/dashboard/wiki',
    },
    {
      id: 'faq_admin',
      element: '#menu-faq',
      title: 'FAQ / Centro de ayuda',
      description: 'Crea categorías y preguntas frecuentes que tus usuarios ven en su sección Ayuda. Los contadores de visitas y de útil o no útil te dicen qué respuestas funcionan.',
      side: 'right',
      navigate: '/dashboard/faq',
    },
    {
      id: 'archive_module',
      element: '#menu-archivo',
      title: 'Archivo',
      description: 'En lugar de borrar, archiva. Si archivas un cliente con proyectos activos, el sistema te ofrece archivar también lo que depende de él. Todo se puede restaurar desde aquí.',
      side: 'right',
      navigate: '/dashboard/archivo',
    },
    {
      id: 'mirror_mode',
      element: '#menu-modo-espejo',
      title: 'Modo Espejo',
      description: 'Entra como cualquier usuario (líder, levantador o cliente) para ver exactamente lo que ve. Sales con el botón Salir del banner amarillo de arriba.',
      side: 'right',
      navigate: '/dashboard/modo-espejo',
    },
    {
      id: 'tour_config',
      element: '#menu-configuracion',
      title: 'Guía de bienvenida',
      description: 'Activa o apaga este recorrido para todos los usuarios y revisa cómo va cada quien.',
      side: 'right',
      navigate: '/dashboard/configuracion',
    },
  ],

  lider_proyecto: [
    {
      id: 'welcome_leader',
      element: '#dashboard-lider',
      title: 'Tu panel',
      description: 'De un vistazo: proyectos activos, evaluaciones en curso, alertas críticas, criterios bloqueantes y fechas límite próximas.',
      side: 'bottom',
      navigate: '/dashboard',
    },
    {
      id: 'projects_list',
      element: '#menu-proyectos',
      title: 'Proyectos',
      description: 'Cada proyecto agrupa un cliente, sus eventos y tu equipo de campo. Es el punto de partida; las evaluaciones se crean dentro de cada evento.',
      side: 'right',
      navigate: '/dashboard/proyectos',
    },
    {
      id: 'create_project',
      element: '#btn-nuevo-proyecto',
      title: 'Crea un proyecto',
      description: 'Define nombre, cliente y ubicación. Agrega la fecha límite, vincula o crea eventos y asigna a tus levantadores (los super admin y los líderes también pueden levantar).',
      side: 'bottom',
    },
    {
      id: 'events_section',
      element: '#menu-eventos',
      title: 'Eventos',
      description: 'En cada evento, la pestaña Evaluaciones tiene Nueva evaluación: eliges plantilla, etapa (Antes, Durante o Después) y un levantador del equipo. Contestar abre el checklist, Revisar la aprobación y Ver la lectura. Ubicación muestra mapa, servicios cercanos y emergencias.',
      side: 'right',
      navigate: '/dashboard/events',
    },
    {
      id: 'evaluations_global_lider',
      element: '#menu-evaluaciones-lider',
      title: 'Evaluaciones',
      description: 'Todas las evaluaciones de tus proyectos, con filtros por cliente, estado y tipo. Acceso directo al evento y descarga del Reporte oficial con folio cuando esté disponible.',
      side: 'right',
      navigate: '/dashboard/evaluaciones',
    },
    {
      id: 'review_evaluations',
      element: '#menu-proyectos',
      title: 'Revisión de evaluaciones',
      description: 'Cuando el levantador termina el checklist, el evento muestra el badge verde "Completada — lista para enviar" y recibes una notificación. Revisas calificaciones y evidencia, y apruebas o regresas.',
      side: 'right',
      navigate: '/dashboard/proyectos',
    },
    {
      id: 'approve_publish',
      element: '#evaluaciones-por-revisar',
      title: 'Aprobar y publicar',
      description: 'Desde "Evaluaciones por revisar" abres cada una. Al publicar se genera un único Reporte oficial con folio. Antes de publicar puedes abrir la Vista previa — BORRADOR (no oficial) desde la revisión.',
      side: 'top',
      navigate: '/dashboard',
    },
    {
      id: 'reports_section',
      element: ['[data-report-section]', '#menu-evaluaciones-lider'],
      title: 'Reportes',
      description: 'Descarga el Reporte oficial desde la evaluación o sus listados; la copia vigente sigue disponible al archivar o regenerar. En la pestaña Después del evento está el Reporte final del evento, con el expediente por etapas y las cadenas de hallazgos.',
      side: 'right',
      navigate: '/dashboard/evaluaciones',
    },
    {
      id: 'help_lider',
      element: '#menu-ayuda-lider',
      title: 'Centro de ayuda',
      description: 'Preguntas frecuentes con buscador. Si una respuesta no te sirve, márcala como no útil y un super admin lo verá.',
      side: 'right',
      navigate: '/dashboard/ayuda',
    },
    {
      id: 'finish_tour',
      element: null,
      title: 'Listo para empezar',
      description: 'Crea tu primer proyecto, agrega un evento y desde ahí asigna evaluaciones. En la app móvil tus levantadores tienen el checklist sin conexión con guardado automático.',
      side: 'center',
      navigate: '/dashboard/proyectos',
    },
  ],

  levantador: [
    {
      id: 'welcome_levantador',
      element: '#dashboard-levantador',
      title: 'Tu panel de campo',
      description: 'Aquí están tus proyectos asignados, tus evaluaciones pendientes o regresadas y los eventos donde participas.',
      side: 'bottom',
      navigate: '/dashboard',
    },
    {
      id: 'my_projects',
      element: '#mis-proyectos-lev-card',
      title: 'Tus proyectos',
      description: 'Solo ves los proyectos donde eres el levantador asignado. Entra a uno para ver sus eventos y las evaluaciones que te tocan.',
      side: 'top',
    },
    {
      id: 'my_events',
      element: ['#my-events-card', '#menu-eventos'],
      title: 'Tus eventos',
      description: 'Cada evento muestra sus checklists pendientes y su nivel de riesgo; los de mañana van primero. Entra al evento y abre la pestaña Evaluaciones para contestar.',
      side: 'top',
    },
    {
      id: 'locations_view',
      element: '#menu-locaciones-lev',
      title: 'Locaciones',
      description: 'Antes de ir, consulta el mapa, el contacto, el aforo máximo y los servicios de emergencia vinculados a la locación.',
      side: 'right',
      navigate: '/dashboard/locations',
    },
    {
      id: 'drafts_card',
      element: '#drafts-card',
      title: 'Checklist en campo',
      description: 'Las preguntas vienen de la plantilla del evento. Puedes dejarlo a la mitad: se guarda solo cada 2.5 segundos y la fecha se prellena con hoy. En las preguntas de archivo subes foto, video o nota de voz como evidencia.',
      side: 'top',
      navigate: '/dashboard',
    },
    {
      id: 'mobile_app_lev',
      element: null,
      title: 'App móvil, recomendada en campo',
      description: 'Desde tu teléfono tienes el checklist sin conexión, con guardado automático y sincronización al recuperar señal. Cuando contestes todo verás "Completada — lista para enviar" y el botón para enviar a revisión.',
      side: 'center',
    },
    {
      id: 'help_lev',
      element: '#menu-ayuda-lev',
      title: 'Centro de ayuda',
      description: 'Si te atoras con el checklist, busca aquí. Las preguntas están agrupadas por tema: empezar, proyectos, evaluaciones, evidencias, eventos y sin conexión.',
      side: 'right',
      navigate: '/dashboard/ayuda',
    },
  ],

  cliente: [
    {
      id: 'welcome_cliente',
      element: '#dashboard-cliente',
      title: 'Tu portal de seguridad',
      description: 'Consulta las evaluaciones publicadas, el semáforo de riesgo, la bitácora de incidentes y el mapa de tus locaciones.',
      side: 'bottom',
      navigate: '/cliente',
    },
    {
      id: 'evaluations',
      element: '#mis-evaluaciones',
      title: 'Tus evaluaciones',
      description: 'Solo se muestran las publicadas. Cada una trae puntaje global, semáforo de riesgo, desglose por sección, los cinco criterios con menor calificación y recomendaciones.',
      side: 'top',
    },
    {
      id: 'incidents_tab',
      element: '#tab-bitacora',
      title: 'Bitácora de incidentes',
      description: 'Filtra por Crítico (criterios bloqueantes con 0) y Mayor (calificación de 2 o menos). Cada incidente muestra el criterio, la recomendación y su evidencia.',
      side: 'top',
    },
    {
      id: 'reports',
      element: ['[data-report-kind="official"]', '#mis-evaluaciones'],
      title: 'Reporte oficial',
      description: 'Un PDF con folio: resumen ejecutivo, detalle técnico, hallazgos y evidencias. Ábrelo desde cada evaluación publicada; si sigue en generación, vuelve a consultar. Las copias archivadas conservan su descarga.',
      side: 'top',
    },
    {
      id: 'map',
      element: '#menu-mapa-cli',
      title: 'Mapa interactivo',
      description: 'Hospitales, rutas de evacuación, zonas de riesgo y puntos críticos marcados por el equipo SOEM durante la inspección.',
      side: 'right',
    },
    {
      id: 'help_cliente',
      element: '#menu-ayuda-cli',
      title: 'Centro de ayuda',
      description: 'Respuestas sobre tus reportes, el semáforo de riesgo y cómo leer los resultados de las evaluaciones.',
      side: 'right',
      navigate: '/cliente/ayuda',
    },
  ],
};

export const TOUR_TIME_ESTIMATE = {
  super_admin:    '6 minutos',
  lider_proyecto: '4 minutos',
  levantador:     '3 minutos',
  cliente:        '2 minutos',
};

export const ROLE_SUBTITLES = {
  super_admin:    'Cuentas con todos los permisos de administración. Te mostramos los primeros pasos.',
  lider_proyecto: 'Aquí gestionarás proyectos, evaluaciones, eventos y tu equipo de campo.',
  levantador:     'Tu herramienta para inspecciones de campo y checklists de seguridad.',
  cliente:        'Consulta tus evaluaciones, reportes y el estado de seguridad de tus locaciones.',
};

export const COMPLETION_CTA = {
  super_admin:    { label: 'Crear mi primer usuario', path: '/dashboard/usuarios/nuevo' },
  lider_proyecto: { label: 'Crear mi primer proyecto', path: '/dashboard/proyectos' },
  levantador:     { label: 'Ver mis proyectos asignados', path: '/dashboard/proyectos' },
  cliente:        { label: 'Ver mis evaluaciones', path: '/cliente' },
};
